import { RcFile } from "antd/lib/upload";
import {
    AnalysisOutput,
    ClosingOutcome,
    Dashboard,
    Project,
    ProjectSummary,
    ProjectAnalysis,
    ProjectAnalysisUpdateRequest,
    ProjectCreateRequest,
    ProjectSourceType,
    ProjectStatus,
    ProjectType,
    ProjectUpdateRequest,
    SaveAnalysisFeedbackRequest,
    SurveySchema,
} from "../../types/ProjectTypes";
import { Document } from "../../types/Document";
import { safeDELETE, safeFormPOST, safeFormPUT, safeGET, safePOST, safePUT, geturl } from "./SafeClient";
import { getFakeProject } from "../../components/dealintake/demo/fakeProjects";

export function listProjects(statusFilter?: ProjectStatus[], abort?: any): Promise<ProjectSummary[]> {
    let url = "/api/project";

    if (statusFilter && statusFilter.length > 0) {
        const statusParams = statusFilter.map((s) => `status[]=${s}`).join("&");
        url += `?${statusParams}`;
    }

    return safeGET(url, abort);
}

export function getProject(projectId: number, abort?: any): Promise<Project> {
    // Demo-only short-circuit: the guided-intake demo creates an in-memory
    // "fake project" so its post-submit redirect can land on the real
    // ProjectView pending state without a backend round-trip.
    const fake = getFakeProject(projectId);
    if (fake) return Promise.resolve(fake);
    return safeGET(`/api/project/${projectId}`, abort);
}

export function createProject(request: ProjectCreateRequest, abort?: any): Promise<Project> {
    const { files, ...data } = request;
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    if (files) {
        for (const file of files) {
            formData.append("files", file);
        }
    }
    return safeFormPOST("/api/project", formData, abort);
}

export function updateProject(
    projectId: number,
    updates: ProjectUpdateRequest,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}`, updates, abort);
}

export function renameProject(
    projectId: number,
    name: string,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/name`, { name }, abort);
}

export function reassignProjectOwner(
    projectId: number,
    userId: number,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/owner/${userId}`, {}, abort);
}

export function reassignProjectGroup(
    projectId: number,
    groupId: number,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/group/${groupId}`, {}, abort);
}

export function deleteProject(projectId: number, abort?: any): Promise<{ success: boolean }> {
    return safeDELETE(`/api/project/${projectId}`, undefined, abort);
}

export function closeProject(
    projectId: number,
    outcome: ClosingOutcome,
    abort?: any
): Promise<Project> {
    return safePOST(`/api/project/${projectId}/close`, { outcome }, abort);
}

export interface AnalysisInputRequest {
    survey: { schema: object; response: object };
    removedDocumentIds: number[];
    files: File[];
}

export function updateAnalysis(
    projectId: number,
    request: AnalysisInputRequest,
    abort?: any
): Promise<Project> {
    return safeFormPUT(
        `/api/project/${projectId}/analysis/update`,
        buildAnalysisFormData(request),
        abort
    );
}

export function submitAnalysis(
    projectId: number,
    request?: AnalysisInputRequest,
    abort?: any
): Promise<ProjectAnalysis> {
    const url = `/api/project/${projectId}/analysis/submit`;
    if (request) {
        return safeFormPOST(url, buildAnalysisFormData(request), abort);
    }
    return safePOST(url, {}, abort);
}

function buildAnalysisFormData(request: AnalysisInputRequest): FormData {
    const { files, ...data } = request;
    const formData = new FormData();
    formData.append("data", JSON.stringify(data));
    for (const file of files) {
        formData.append("files", file);
    }
    return formData;
}

export function getSurveySchema(
    projectId: number,
    abort?: any
): Promise<SurveySchema> {
    return safeGET(`/api/project/${projectId}/survey-schema`, abort);
}

export function getInitialSurveySchema(
    projectType: ProjectType,
    options?: { source?: ProjectSourceType },
    abort?: any
): Promise<SurveySchema> {
    const qs = options?.source
        ? `?source=${encodeURIComponent(options.source)}`
        : "";
    return safeGET(`/api/project/survey-schema/${projectType}${qs}`, abort);
}

export function updateProjectAnalysis(
    projectId: number,
    analysisVersion: number,
    updates: ProjectAnalysisUpdateRequest,
    abort?: any
): Promise<ProjectAnalysis> {
    return safePUT(`/api/project/${projectId}/analysis/${analysisVersion}`, updates, abort);
}

export function uploadProjectDocument(
    projectId: number,
    file: RcFile,
    abort?: any
): Promise<Document> {
    const formData = new FormData();
    formData.append("file", file);

    return safeFormPOST(`/api/project/${projectId}/document`, formData, abort);
}

export function getAnalysisOutput(
    projectId: number,
    analysisVersion: number,
    abort?: any
): Promise<AnalysisOutput> {
    return safeGET(`/api/project/${projectId}/analysis/${analysisVersion}/output`, abort);
}

export function getAnalysisScores(
    projectId: number,
    abort?: any
): Promise<Array<{ version: number; orderScore: number }>> {
    return safeGET(`/api/project/${projectId}/scores`, abort);
}

export function getAnalysisOutputContentUrl(projectId: number, analysisVersion: number): string {
    return geturl(`/api/project/${projectId}/analysis/${analysisVersion}/output/content`);
}

export function getDashboard(abort?: any): Promise<Dashboard> {
    return safeGET("/api/project/dashboard", abort);
}

export function requestAnalysisChange(
    projectId: number,
    message: string,
    abort?: any
): Promise<ProjectAnalysis> {
    return safePOST(
        `/api/project/${projectId}/analysis/request-change`,
        { message },
        abort
    );
}

export function saveAnalysisOutput(
    projectId: number,
    analysisVersion: number,
    request: { draft: boolean; output: AnalysisOutput },
    abort?: any
): Promise<ProjectAnalysis> {
    return safePOST(`/api/project/${projectId}/analysis/${analysisVersion}/output`, request, abort);
}

export function saveAnalysisFeedback(
    projectId: number,
    analysisVersion: number,
    request: SaveAnalysisFeedbackRequest,
    abort?: any
): Promise<ProjectAnalysis> {
    return safePOST(
        `/api/project/${projectId}/analysis/${analysisVersion}/feedback`,
        request,
        abort
    );
}

export function removeAnalysisFeedback(
    projectId: number,
    analysisVersion: number,
    path: string,
    abort?: any
): Promise<ProjectAnalysis> {
    return safeDELETE(
        `/api/project/${projectId}/analysis/${analysisVersion}/feedback/${encodeURIComponent(path)}`,
        undefined,
        abort
    );
}

// ── Attachment endpoints ──────────────────────────────────────────────

export function uploadAnalysisAttachment(
    projectId: number,
    analysisVersion: number,
    file: RcFile,
    abort?: any
): Promise<Document> {
    const formData = new FormData();
    formData.append("file", file);

    return safeFormPOST(
        `/api/project/${projectId}/analysis/${analysisVersion}/attachment`,
        formData,
        abort
    );
}

export function getAttachmentDownloadUrl(
    projectId: number,
    analysisVersion: number,
    documentId: number
): string {
    return `/api/project/${projectId}/analysis/${analysisVersion}/attachment/${documentId}`;
}

export function deleteAnalysisAttachment(
    projectId: number,
    analysisVersion: number,
    documentId: number,
    abort?: any
): Promise<{ success: boolean }> {
    return safeDELETE(
        `/api/project/${projectId}/analysis/${analysisVersion}/attachment/${documentId}`,
        undefined,
        abort
    );
}

export function setCollaborator(
    projectId: number,
    userId: number,
    canEdit: boolean,
    abort?: any
): Promise<Project> {
    return safePUT(`/api/project/${projectId}/collaborator/${userId}`, { canEdit }, abort);
}

export function removeCollaborator(
    projectId: number,
    userId: number,
    abort?: any
): Promise<Project> {
    return safeDELETE(`/api/project/${projectId}/collaborator/${userId}`, undefined, abort);
}
